import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Building, CreditCard, Hash, Percent, DollarSign, MapPin, Landmark } from "lucide-react";

export default function DefinicionesPage() {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState("bancos");
  const [scope, setScope] = useState(["*"]);

  useEffect(() => {
    axios.get(`${API}/definiciones/my-scope`, auth)
      .then(r => {
        const s = r.data?.scope || ["*"];
        setScope(s);
        // Si el usuario NO tiene "*" y el tab por defecto ("bancos") no está permitido,
        // saltamos al primer submódulo permitido para no dejarlo en una tab vacía.
        if (!s.includes("*") && !s.includes("bancos") && s.length) {
          setTab(s[0]);
        }
      })
      .catch(() => setScope(["*"]));
    /* eslint-disable-next-line */
  }, []);

  const showTab = (key) => scope.includes("*") || scope.includes(key);

  return (
    <div className="space-y-6" data-testid="definiciones-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900">Definiciones</h1>
        <p className="text-green-700/70 mt-1">Catálogos base del sistema</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-green-50 border border-green-100 flex-wrap h-auto">
          {showTab("bancos") && <TabsTrigger value="bancos" data-testid="tab-bancos"><Building className="w-4 h-4 mr-2" />Bancos</TabsTrigger>}
          {showTab("cuentas") && <TabsTrigger value="cuentas" data-testid="tab-cuentas"><CreditCard className="w-4 h-4 mr-2" />Cuentas Bancarias</TabsTrigger>}
          {showTab("impuestos") && <TabsTrigger value="impuestos" data-testid="tab-impuestos"><Percent className="w-4 h-4 mr-2" />Tipos de Impuesto</TabsTrigger>}
          {showTab("codigos") && <TabsTrigger value="codigos" data-testid="tab-codigos"><Hash className="w-4 h-4 mr-2" />Códigos Presupuestarios</TabsTrigger>}
          {showTab("cotizaciones") && <TabsTrigger value="cotizaciones" data-testid="tab-cotizaciones"><DollarSign className="w-4 h-4 mr-2" />Cotización de Moneda</TabsTrigger>}
          {showTab("sectores") && <TabsTrigger value="sectores" data-testid="tab-sectores"><MapPin className="w-4 h-4 mr-2" />Sectores</TabsTrigger>}
          {showTab("municipios") && <TabsTrigger value="municipios" data-testid="tab-municipios"><Landmark className="w-4 h-4 mr-2" />Municipios</TabsTrigger>}
        </TabsList>
        {showTab("bancos") && <TabsContent value="bancos"><Bancos auth={auth} API={API} /></TabsContent>}
        {showTab("cuentas") && <TabsContent value="cuentas"><Cuentas auth={auth} API={API} /></TabsContent>}
        {showTab("impuestos") && <TabsContent value="impuestos"><TiposImpuesto auth={auth} API={API} /></TabsContent>}
        {showTab("codigos") && <TabsContent value="codigos"><Codigos auth={auth} API={API} /></TabsContent>}
        {showTab("cotizaciones") && <TabsContent value="cotizaciones"><Cotizaciones auth={auth} API={API} /></TabsContent>}
        {showTab("sectores") && <TabsContent value="sectores"><Sectores auth={auth} API={API} /></TabsContent>}
        {showTab("municipios") && <TabsContent value="municipios"><Municipios auth={auth} API={API} /></TabsContent>}
      </Tabs>
    </div>
  );
}

function Bancos({ auth, API }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ codigo: "", nombre: "" });
  const load = () => axios.get(`${API}/definiciones/bancos`, auth).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await axios.post(`${API}/definiciones/bancos`, form, auth); toast.success("Banco creado"); setOpen(false); setForm({ codigo:"", nombre:"" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar?")) return; await axios.delete(`${API}/definiciones/bancos/${id}`, auth); toast.success("Eliminado"); load(); };
  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-green-900">Bancos ({items.length})</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-banco" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nuevo</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo banco</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Código*</Label><Input data-testid="input-banco-codigo" value={form.codigo} onChange={e => setForm({...form, codigo:e.target.value})} placeholder="0102" /></div>
              <div><Label>Nombre*</Label><Input data-testid="input-banco-nombre" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} /></div>
            </div>
            <DialogFooter><Button data-testid="btn-save-banco" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900"><tr><th className="text-left p-2.5">Código</th><th className="text-left p-2.5">Nombre</th><th className="text-right p-2.5">Acciones</th></tr></thead>
          <tbody>{items.map(b => (
            <tr key={b.id} className="border-b border-green-50 hover:bg-green-50/50">
              <td className="p-2.5 font-mono font-semibold">{b.codigo}</td><td className="p-2.5">{b.nombre}</td>
              <td className="p-2.5 text-right"><Button data-testid={`btn-del-banco-${b.codigo}`} size="icon" variant="ghost" onClick={() => del(b.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}

function Cuentas({ auth, API }) {
  const [items, setItems] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const empty = { banco_id:"", numero_cuenta:"", tipo:"transferencia", rif:"", descripcion:"" };
  const [form, setForm] = useState(empty);
  const load = async () => {
    const [a,b] = await Promise.all([axios.get(`${API}/definiciones/cuentas-bancarias`, auth), axios.get(`${API}/definiciones/bancos`, auth)]);
    setItems(a.data); setBancos(b.data);
  };
  useEffect(() => { load(); }, []);
  const closeDialog = () => { setOpen(false); setEditingId(null); setForm(empty); };
  const save = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/definiciones/cuentas-bancarias/${editingId}`, form, auth);
        toast.success("Cuenta actualizada");
      } else {
        await axios.post(`${API}/definiciones/cuentas-bancarias`, form, auth);
        toast.success("Cuenta creada");
      }
      closeDialog(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (c) => {
    setEditingId(c.id);
    setForm({ banco_id: c.banco_id || "", numero_cuenta: c.numero_cuenta || "", tipo: c.tipo || "transferencia", rif: c.rif || "", descripcion: c.descripcion || "" });
    setOpen(true);
  };
  const del = async (id) => { if(!window.confirm("¿Eliminar?")) return; await axios.delete(`${API}/definiciones/cuentas-bancarias/${id}`, auth); load(); };
  const tipoLabel = (t) => ({
    transferencia: "Transferencia",
    pago_movil: "Pago Móvil",
    punto_venta: "Punto de Venta",
    deposito: "Depósito",
    // legados anteriores:
    corriente: "Corriente",
    ahorro: "Ahorro",
  }[t] || t || "-");
  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-green-900">Cuentas Bancarias ({items.length})</h3>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) closeDialog(); }}>
          <DialogTrigger asChild><Button data-testid="btn-add-cuenta" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nueva</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Editar cuenta bancaria" : "Nueva cuenta bancaria"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Banco*</Label>
                <Select value={form.banco_id} onValueChange={v => setForm({...form, banco_id:v})}>
                  <SelectTrigger data-testid="input-cuenta-banco"><SelectValue placeholder="Seleccione banco" /></SelectTrigger>
                  <SelectContent>{bancos.map(b => <SelectItem key={b.id} value={b.id}>{b.codigo} - {b.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nº Cuenta*</Label><Input data-testid="input-cuenta-numero" value={form.numero_cuenta} onChange={e => setForm({...form, numero_cuenta:e.target.value})} /></div>
              <div><Label>RIF</Label><Input data-testid="input-cuenta-rif" value={form.rif} onChange={e => setForm({...form, rif:e.target.value})} placeholder="Ej. J-123456789" /></div>
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo:v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccione tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="pago_movil">Pago Móvil</SelectItem>
                    <SelectItem value="punto_venta">Punto de Venta</SelectItem>
                    <SelectItem value="deposito">Depósito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Descripción</Label><Input value={form.descripcion} onChange={e => setForm({...form, descripcion:e.target.value})} /></div>
            </div>
            <DialogFooter><Button data-testid="btn-save-cuenta" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editingId ? "Actualizar" : "Guardar"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900"><tr><th className="text-left p-2.5">Banco</th><th className="text-left p-2.5">Nº Cuenta</th><th className="text-left p-2.5">RIF</th><th className="text-left p-2.5">Tipo</th><th className="text-right p-2.5">Acciones</th></tr></thead>
          <tbody>{items.map(c => (
            <tr key={c.id} className="border-b border-green-50">
              <td className="p-2.5">{c.banco_nombre}</td>
              <td className="p-2.5 font-mono">{c.numero_cuenta}</td>
              <td className="p-2.5 font-mono">{c.rif || "-"}</td>
              <td className="p-2.5">{tipoLabel(c.tipo)}</td>
              <td className="p-2.5 text-right whitespace-nowrap">
                <Button size="icon" variant="ghost" onClick={() => edit(c)} title="Editar" data-testid={`btn-edit-cuenta-${c.id}`}><Edit className="w-4 h-4 text-blue-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}

function Codigos({ auth, API }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ codigo:"", nombre:"" });
  const load = () => axios.get(`${API}/definiciones/codigos-presupuestarios`, auth).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await axios.post(`${API}/definiciones/codigos-presupuestarios`, form, auth); toast.success("Creado"); setOpen(false); setForm({codigo:"",nombre:""}); load(); } catch(e){ toast.error(e.response?.data?.detail||"Error"); } };
  const del = async (id) => { if(!window.confirm("¿Eliminar?")) return; await axios.delete(`${API}/definiciones/codigos-presupuestarios/${id}`, auth); load(); };
  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-green-900">Códigos Presupuestarios ({items.length})</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-codigo" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nuevo</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo código presupuestario</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Código*</Label><Input data-testid="input-cod-codigo" value={form.codigo} onChange={e => setForm({...form,codigo:e.target.value})} /></div>
              <div><Label>Nombre/Descripción*</Label><Input data-testid="input-cod-nombre" value={form.nombre} onChange={e => setForm({...form,nombre:e.target.value})} /></div>
            </div>
            <DialogFooter><Button data-testid="btn-save-codigo" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900"><tr><th className="text-left p-2.5">Código</th><th className="text-left p-2.5">Descripción</th><th className="text-right p-2.5">Acciones</th></tr></thead>
          <tbody>{items.map(c => <tr key={c.id} className="border-b border-green-50"><td className="p-2.5 font-mono">{c.codigo}</td><td className="p-2.5">{c.nombre}</td><td className="p-2.5 text-right"><Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td></tr>)}</tbody>
        </table>
      </div>
    </Card>
  );
}


function TiposImpuesto({ auth, API }) {
  const [items, setItems] = useState([]);
  const [codigos, setCodigos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre:"", descripcion:"", tasa:0, codigo_presupuestario_id:"", aplica_descuento:false, porcentaje_descuento:0 });
  const load = async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API}/definiciones/tipos-impuesto`, auth),
      axios.get(`${API}/definiciones/codigos-presupuestarios`, auth),
    ]);
    setItems(a.data); setCodigos(b.data);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...form, tasa: Number(form.tasa)||0, porcentaje_descuento: Number(form.porcentaje_descuento)||0 };
      if (editing) await axios.put(`${API}/definiciones/tipos-impuesto/${editing.id}`, payload, auth);
      else await axios.post(`${API}/definiciones/tipos-impuesto`, payload, auth);
      toast.success(editing ? "Actualizado" : "Tipo de impuesto creado");
      setOpen(false); setEditing(null); setForm({ nombre:"", descripcion:"", tasa:0, codigo_presupuestario_id:"", aplica_descuento:false, porcentaje_descuento:0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (t) => { setEditing(t); setForm({ ...t, codigo_presupuestario_id: t.codigo_presupuestario_id || "" }); setOpen(true); };
  const del = async (id) => { if(!window.confirm("¿Eliminar?")) return; await axios.delete(`${API}/definiciones/tipos-impuesto/${id}`, auth); load(); };
  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-green-900">Tipos de Impuesto ({items.length})</h3>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if(!o){ setEditing(null); setForm({ nombre:"", descripcion:"", tasa:0, codigo_presupuestario_id:"", aplica_descuento:false, porcentaje_descuento:0 }); } }}>
          <DialogTrigger asChild><Button data-testid="btn-add-impuesto" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nuevo</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} tipo de impuesto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre*</Label><Input data-testid="input-imp-nombre" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} placeholder="Ej: Aseo Urbano Domiciliario" /></div>
              <div><Label>Descripción</Label><Input value={form.descripcion||""} onChange={e => setForm({...form, descripcion:e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tasa (%)</Label><Input data-testid="input-imp-tasa" type="number" step="0.01" value={form.tasa} onChange={e => setForm({...form, tasa:e.target.value})} /></div>
                <div><Label>Código presupuestario</Label>
                  <Select value={form.codigo_presupuestario_id} onValueChange={v => setForm({...form, codigo_presupuestario_id:v})}>
                    <SelectTrigger data-testid="input-imp-codigo"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{codigos.map(c => <SelectItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="aplica-desc" checked={form.aplica_descuento} onChange={e => setForm({...form, aplica_descuento:e.target.checked})} className="accent-green-700 w-4 h-4" />
                <Label htmlFor="aplica-desc" className="cursor-pointer">Aplica descuento por pronto pago</Label>
              </div>
              {form.aplica_descuento && (
                <div><Label>Porcentaje descuento (%)</Label><Input type="number" step="0.01" value={form.porcentaje_descuento} onChange={e => setForm({...form, porcentaje_descuento:e.target.value})} /></div>
              )}
            </div>
            <DialogFooter><Button data-testid="btn-save-impuesto" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editing ? "Actualizar" : "Crear"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900"><tr>
            <th className="text-left p-2.5">Nombre</th><th className="text-left p-2.5">Descripción</th>
            <th className="text-right p-2.5">Tasa (%)</th><th className="text-left p-2.5">Código Presup.</th>
            <th className="text-center p-2.5">Descuento</th><th className="text-right p-2.5">Acciones</th>
          </tr></thead>
          <tbody>{items.map(t => (
            <tr key={t.id} className="border-b border-green-50 hover:bg-green-50/40">
              <td className="p-2.5 font-medium">{t.nombre}</td>
              <td className="p-2.5 text-slate-600">{t.descripcion||"-"}</td>
              <td className="p-2.5 text-right font-mono">{(t.tasa||0).toFixed(2)}</td>
              <td className="p-2.5 text-xs font-mono">{t.codigo_presupuestario_nombre||"-"}</td>
              <td className="p-2.5 text-center">{t.aplica_descuento ? <span className="text-orange-600 font-semibold">{t.porcentaje_descuento}%</span> : <span className="text-slate-400">-</span>}</td>
              <td className="p-2.5 text-right">
                <Button size="icon" variant="ghost" onClick={() => edit(t)}><Edit className="w-4 h-4 text-blue-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </td>
            </tr>
          ))}{!items.length && <tr><td colSpan="6" className="p-8 text-center text-slate-400">Sin tipos de impuesto</td></tr>}</tbody>
        </table>
      </div>
    </Card>
  );
}

function Cotizaciones({ auth, API }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ euro:0, dolar:0, otro:0, valor_unidad_cuenta:0, estatus:"Activo", fecha:"" });
  const load = () => axios.get(`${API}/definiciones/cotizaciones-moneda`, auth).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...form,
        euro: Number(form.euro)||0, dolar: Number(form.dolar)||0,
        otro: Number(form.otro)||0, valor_unidad_cuenta: Number(form.valor_unidad_cuenta)||0,
      };
      if (editing) await axios.put(`${API}/definiciones/cotizaciones-moneda/${editing.id}`, payload, auth);
      else await axios.post(`${API}/definiciones/cotizaciones-moneda`, payload, auth);
      toast.success(editing ? "Actualizada" : "Cotización creada");
      setOpen(false); setEditing(null); setForm({ euro:0, dolar:0, otro:0, valor_unidad_cuenta:0, estatus:"Activo", fecha:"" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (c) => { setEditing(c); setForm({ ...c, fecha: c.fecha || "" }); setOpen(true); };
  const del = async (id) => { if(!window.confirm("¿Eliminar cotización?")) return; await axios.delete(`${API}/definiciones/cotizaciones-moneda/${id}`, auth); load(); };
  const activar = async (c) => { await axios.put(`${API}/definiciones/cotizaciones-moneda/${c.id}`, { estatus: "Activo" }, auth); toast.success("Cotización activada"); load(); };
  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-green-900">Cotización de Moneda ({items.length})</h3>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if(!o){ setEditing(null); setForm({ euro:0, dolar:0, otro:0, valor_unidad_cuenta:0, estatus:"Activo", fecha:"" }); } }}>
          <DialogTrigger asChild><Button data-testid="btn-add-cotizacion" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nueva</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nueva"} cotización</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha</Label><Input data-testid="input-cot-fecha" type="date" value={form.fecha} onChange={e => setForm({...form, fecha:e.target.value})} /></div>
              <div><Label>Estatus</Label>
                <Select value={form.estatus} onValueChange={v => setForm({...form, estatus:v})}>
                  <SelectTrigger data-testid="input-cot-estatus"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Activo">Activo (vigente)</SelectItem>
                    <SelectItem value="Histórico">Histórico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Dólar (USD/Bs.)</Label><Input data-testid="input-cot-dolar" type="number" step="0.0001" value={form.dolar} onChange={e => setForm({...form, dolar:e.target.value})} /></div>
              <div><Label>Euro (EUR/Bs.)</Label><Input data-testid="input-cot-euro" type="number" step="0.0001" value={form.euro} onChange={e => setForm({...form, euro:e.target.value})} /></div>
              <div><Label>Otro</Label><Input type="number" step="0.0001" value={form.otro} onChange={e => setForm({...form, otro:e.target.value})} /></div>
              <div><Label>Unidad de Cuenta</Label><Input data-testid="input-cot-uc" type="number" step="0.0001" value={form.valor_unidad_cuenta} onChange={e => setForm({...form, valor_unidad_cuenta:e.target.value})} /></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Si marca "Activo", las demás cotizaciones se moverán automáticamente a Histórico.</p>
            <DialogFooter><Button data-testid="btn-save-cotizacion" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editing ? "Actualizar" : "Crear"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900"><tr>
            <th className="text-left p-2.5">Fecha</th><th className="text-right p-2.5">USD</th><th className="text-right p-2.5">EUR</th>
            <th className="text-right p-2.5">Otro</th><th className="text-right p-2.5">Unidad Cuenta</th>
            <th className="text-center p-2.5">Estatus</th><th className="text-right p-2.5">Acciones</th>
          </tr></thead>
          <tbody>{items.map(c => (
            <tr key={c.id} className="border-b border-green-50 hover:bg-green-50/40">
              <td className="p-2.5 font-mono">{c.fecha||"-"}</td>
              <td className="p-2.5 text-right font-mono">{(c.dolar||0).toFixed(4)}</td>
              <td className="p-2.5 text-right font-mono">{(c.euro||0).toFixed(4)}</td>
              <td className="p-2.5 text-right font-mono">{(c.otro||0).toFixed(4)}</td>
              <td className="p-2.5 text-right font-mono">{(c.valor_unidad_cuenta||0).toFixed(4)}</td>
              <td className="p-2.5 text-center">
                {c.estatus === "Activo"
                  ? <span className="inline-block px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-xs font-semibold">Activo</span>
                  : <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs">Histórico</span>}
              </td>
              <td className="p-2.5 text-right">
                {c.estatus !== "Activo" && <Button size="sm" variant="ghost" onClick={() => activar(c)} className="text-orange-600" data-testid={`btn-activar-${c.id}`}>Activar</Button>}
                <Button size="icon" variant="ghost" onClick={() => edit(c)}><Edit className="w-4 h-4 text-blue-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </td>
            </tr>
          ))}{!items.length && <tr><td colSpan="7" className="p-8 text-center text-slate-400">Sin cotizaciones registradas</td></tr>}</tbody>
        </table>
      </div>
    </Card>
  );
}

function Sectores({ auth, API }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "" });
  const [search, setSearch] = useState("");

  const load = () => axios.get(`${API}/definiciones/sectores`, auth).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Indique el nombre del sector"); return; }
    try {
      if (editing) await axios.put(`${API}/definiciones/sectores/${editing.id}`, form, auth);
      else await axios.post(`${API}/definiciones/sectores`, form, auth);
      toast.success(editing ? "Sector actualizado" : "Sector creado");
      setOpen(false); setEditing(null); setForm({ nombre: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (s) => { setEditing(s); setForm({ nombre: s.nombre }); setOpen(true); };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar sector?")) return;
    await axios.delete(`${API}/definiciones/sectores/${id}`, auth);
    toast.success("Eliminado");
    load();
  };

  const filtered = search ? items.filter(s => s.nombre.toLowerCase().includes(search.toLowerCase())) : items;

  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display font-bold text-lg text-green-900">Sectores ({items.length})</h3>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ nombre: "" }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-sector" className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="w-4 h-4 mr-1" />Nuevo Sector
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} sector</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nombre del sector*</Label>
                <Input data-testid="input-sector-nombre" value={form.nombre} onChange={e => setForm({ nombre: e.target.value })} placeholder="Ej. Centro, Las Acacias, Parroquia La Vega…" autoFocus />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="btn-save-sector" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">
                {editing ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 mb-4 max-w-md">
        <MapPin className="w-4 h-4 text-green-700" />
        <Input data-testid="input-search-sector" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar sector…" className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-8" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900">
            <tr>
              <th className="text-left p-2.5">Nombre del Sector</th>
              <th className="text-right p-2.5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} className="border-b border-green-50 hover:bg-green-50/50" data-testid={`row-sector-${s.id}`}>
                <td className="p-2.5 font-medium text-green-900">{s.nombre}</td>
                <td className="p-2.5 text-right">
                  <Button size="icon" variant="ghost" onClick={() => edit(s)} title="Editar"><Edit className="w-4 h-4 text-blue-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(s.id)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan="2" className="p-8 text-center text-slate-400">
                {search ? "Sin coincidencias" : "Sin sectores registrados. Use \"Nuevo Sector\" para agregar el primero."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


function Municipios({ auth, API }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", estado_nombre: "", parroquias: [] });
  const [parroquiaInput, setParroquiaInput] = useState("");
  const [search, setSearch] = useState("");

  const load = () => axios.get(`${API}/definiciones/municipios`, auth).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ nombre: "", estado_nombre: "", parroquias: [] }); setParroquiaInput(""); };

  const addParroquia = () => {
    const n = parroquiaInput.trim();
    if (!n) return;
    setForm({ ...form, parroquias: [...form.parroquias, { id: `tmp-${Date.now()}-${Math.random()}`, nombre: n }] });
    setParroquiaInput("");
  };
  const removeParroquia = (idx) => {
    setForm({ ...form, parroquias: form.parroquias.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Indique el nombre del municipio"); return; }
    if (!form.estado_nombre.trim()) { toast.error("Indique el estado"); return; }
    try {
      const payload = {
        nombre: form.nombre,
        estado_nombre: form.estado_nombre,
        parroquias: form.parroquias.map(p => ({ id: String(p.id).startsWith("tmp-") ? undefined : p.id, nombre: p.nombre })),
      };
      if (editing) await axios.put(`${API}/definiciones/municipios/${editing.id}`, payload, auth);
      else await axios.post(`${API}/definiciones/municipios`, payload, auth);
      toast.success(editing ? "Municipio actualizado" : "Municipio creado");
      setOpen(false); setEditing(null); resetForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (m) => {
    setEditing(m);
    setForm({ nombre: m.nombre, estado_nombre: m.estado_nombre || "", parroquias: m.parroquias || [] });
    setParroquiaInput("");
    setOpen(true);
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar municipio?")) return;
    await axios.delete(`${API}/definiciones/municipios/${id}`, auth);
    toast.success("Eliminado");
    load();
  };

  const filtered = items.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.nombre.toLowerCase().includes(q)
      || (m.estado_nombre||"").toLowerCase().includes(q)
      || (m.parroquias||[]).some(p => (p.nombre||"").toLowerCase().includes(q));
  });

  return (
    <Card className="p-5 border-green-100 mt-4">
      <div className="flex justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display font-bold text-lg text-green-900">Municipios ({items.length})</h3>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-municipio" className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="w-4 h-4 mr-1" />Nuevo Municipio
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} municipio</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Estado*</Label>
                <Input data-testid="input-municipio-estado" value={form.estado_nombre} onChange={e => setForm({ ...form, estado_nombre: e.target.value })} placeholder="Ej. Miranda, Distrito Capital, Zulia…" />
              </div>
              <div>
                <Label>Nombre del municipio*</Label>
                <Input data-testid="input-municipio-nombre" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Libertador, Sucre, Chacao…" />
              </div>

              <div className="border-t pt-3">
                <Label>Parroquias ({form.parroquias.length})</Label>
                <p className="text-xs text-slate-500 mb-2">Puede agregar tantas parroquias como necesite (se permiten nombres duplicados).</p>
                <div className="flex gap-2">
                  <Input
                    data-testid="input-parroquia-nombre"
                    value={parroquiaInput}
                    onChange={e => setParroquiaInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addParroquia(); } }}
                    placeholder="Nombre de la parroquia"
                  />
                  <Button type="button" data-testid="btn-add-parroquia" onClick={addParroquia} className="bg-green-700 hover:bg-green-800 text-white shrink-0">
                    <Plus className="w-4 h-4 mr-1" />Agregar
                  </Button>
                </div>

                {form.parroquias.length > 0 && (
                  <div className="mt-3 space-y-1.5 max-h-52 overflow-y-auto">
                    {form.parroquias.map((p, idx) => (
                      <div key={p.id || idx} className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-1.5" data-testid={`parroquia-item-${idx}`}>
                        <span className="text-sm text-green-900">{idx + 1}. {p.nombre}</span>
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeParroquia(idx)} title="Quitar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="btn-save-municipio" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">
                {editing ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 mb-4 max-w-md">
        <Landmark className="w-4 h-4 text-green-700" />
        <Input data-testid="input-search-municipio" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar municipio, estado o parroquia…" className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-8" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900">
            <tr>
              <th className="text-left p-2.5">Municipio</th>
              <th className="text-left p-2.5">Estado</th>
              <th className="text-left p-2.5">Parroquias</th>
              <th className="text-right p-2.5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.id} className="border-b border-green-50 hover:bg-green-50/50 align-top" data-testid={`row-municipio-${m.id}`}>
                <td className="p-2.5 font-medium text-green-900">{m.nombre}</td>
                <td className="p-2.5 text-slate-600">{m.estado_nombre || "—"}</td>
                <td className="p-2.5">
                  {(m.parroquias && m.parroquias.length > 0) ? (
                    <div className="flex flex-wrap gap-1.5 max-w-2xl">
                      {m.parroquias.map((p, idx) => (
                        <span key={p.id || idx} className="inline-block px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-xs font-medium">
                          {p.nombre}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Sin parroquias</span>
                  )}
                </td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => edit(m)} title="Editar"><Edit className="w-4 h-4 text-blue-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(m.id)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan="4" className="p-8 text-center text-slate-400">
                {search ? "Sin coincidencias" : "Sin municipios registrados. Use \"Nuevo Municipio\" para agregar el primero."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

